import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { Input, OtpInput } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import {
  ShowPasswordIcon as HidePasswordIcon,
  PasswordShownIcon as ShowPasswordIcon
} from '@/resources';
import { TestIds } from '@/shared/test-ids';

type MfaReAuthFormProps = {
  reAuthStep: 'password' | 'totp';
  reAuthPassword: string;
  onReAuthPasswordChange: (value: string) => void;
  showReAuthPassword: boolean;
  onToggleShowReAuthPassword: () => void;
  isLoading: boolean;
  onConfirm: () => void;
  reAuthTotpCode: string;
  onReAuthTotpChange: (value: string) => void;
  onVerifyTotp: () => void;
  onCancel?: () => void;
};

export const MfaReAuthForm = ({
  reAuthStep,
  reAuthPassword,
  onReAuthPasswordChange,
  showReAuthPassword,
  onToggleShowReAuthPassword,
  isLoading,
  onConfirm,
  reAuthTotpCode,
  onReAuthTotpChange,
  onVerifyTotp,
  onCancel
}: MfaReAuthFormProps) => {
  const [t] = useTranslation('common');

  const getShowPasswordIcon = (fieldValue: string, showPwd: boolean, toggle: () => void) => (
    <div className='absolute right-0 flex cursor-pointer items-center' onClick={toggle}>
      {showPwd ? (
        <ShowPasswordIcon
          className={`mr-2 text-xl ${fieldValue.length > 0 ? 'fill-primary-black' : 'fill-secondary-gray'}`}
        />
      ) : (
        <HidePasswordIcon
          className={`mr-2 text-xl ${fieldValue.length > 0 ? 'fill-primary-black' : 'fill-secondary-gray'}`}
        />
      )}
    </div>
  );

  if (reAuthStep === 'password') {
    return (
      <div className='flex flex-col gap-3'>
        <Label
          text={t('mfaReAuthRequired')}
          type={LABEL_TYPE.PARAGRAPH1}
          className='!text-secondary-gray'
        />
        <div className={clsx(onCancel === undefined ? 'md:w-full' : 'md:w-1/2 md:pr-6')}>
          <Input
            id='reAuthPassword'
            label={t('password')}
            type={showReAuthPassword ? 'text' : 'password'}
            value={reAuthPassword}
            handleChange={onReAuthPasswordChange}
            rightSideIcon={getShowPasswordIcon(
              reAuthPassword,
              showReAuthPassword,
              onToggleShowReAuthPassword
            )}
          />
        </div>

        <div className='flex gap-3'>
          <Button
            variant={ButtonVariant.PRIMARY}
            disabled={!reAuthPassword || isLoading}
            handleOnClick={onConfirm}
            className='w-fit'>
            {t('confirm')}
          </Button>
          {onCancel && (
            <Button
              variant={ButtonVariant.SECONDARY}
              disabled={isLoading}
              handleOnClick={onCancel}
              className='w-fit'>
              {t('cancel')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      <Label
        text={t('mfaEnterCode')}
        type={LABEL_TYPE.PARAGRAPH1}
        className='!text-secondary-gray'
      />
      <OtpInput
        value={reAuthTotpCode}
        onChange={onReAuthTotpChange}
        disabled={isLoading}
        testIdPrefix={TestIds.mfa.reAuthOtpInput}
        onComplete={onVerifyTotp}
      />
      {onCancel && (
        <div className='flex gap-3'>
          <Button
            variant={ButtonVariant.SECONDARY}
            disabled={isLoading}
            handleOnClick={onCancel}
            className='w-fit'>
            {t('cancel')}
          </Button>
        </div>
      )}
    </div>
  );
};
